import {TerserOptions} from "vite";
import {LoggerConfig} from "@xcons/common";
import {ProcessorFunction} from "./process-options";

/**
 * Sass compiler options (subset of sass.Options)
 */
export interface SassOptions {
    loadPaths?: string[];
    charset?: boolean;
    quietDeps?: boolean;
    verbose?: boolean;
    sourceMap?: boolean;
    sourceMapIncludeSources?: boolean;
}

/**
 * Main plugin options interface
 */
export interface XConVitePluginOptions {
    // Processing options
    minifyTemplates?: boolean;
    minifyStyles?: boolean;
    removeComments?: boolean;
    watchFiles?: boolean;
    preserveWhitespace?: boolean;

    // SCSS/SASS compilation options
    compileSass?: boolean;
    sassOptions?: SassOptions;

    // Resource options
    resourceBaseUrl?: string;
    processResources?: boolean;

    // Logging options
    showProcessedFiles?: boolean;

    logger?: Partial<LoggerConfig> & {
        logLevel?: 'debug' | 'info' | 'warn' | 'error' | 'silent';
    };

    // File extension options
    fileExtensions?: string[];
    templateExtensions?: string[];
    styleExtensions?: string[];

    // Minification with Terser
    useTerser?: boolean;
    terserOptions?: TerserOptions;

    // Advanced options
    customProcessors?: ProcessorFunction[];
    excludeFiles?: string[];
    includeFiles?: string[];

    // Development options
    development?: boolean;
    sourceMap?: boolean;
}

/**
 * Get automatic loadPaths with node_modules detection
 */
function getDefaultLoadPaths(): string[] {
    const path = require('path');
    const fs = require('fs');
    const loadPaths: string[] = [];

    // Get current working directory
    const cwd = process.cwd();

    // Add node_modules
    const nodeModulesPath = path.resolve(cwd, 'node_modules');
    if (fs.existsSync(nodeModulesPath)) {
        loadPaths.push(nodeModulesPath);
    }

    // Add src directory
    const srcPath = path.resolve(cwd, 'src');
    if (fs.existsSync(srcPath)) {
        loadPaths.push(srcPath);
    }

    // Add src/styles directory
    const stylesPath = path.resolve(cwd, 'src/styles');
    if (fs.existsSync(stylesPath)) {
        loadPaths.push(stylesPath);
    }

    return loadPaths;
}

/**
 * Create default options with automatic SCSS configuration
 */
export function createDefaultOptions(userOptions: XConVitePluginOptions = {}): Required<XConVitePluginOptions> {
    // Get automatic loadPaths
    const defaultLoadPaths = getDefaultLoadPaths();

    // Merge user loadPaths with defaults
    const finalLoadPaths = [
        ...defaultLoadPaths,
        ...(userOptions.sassOptions?.loadPaths || [])
    ];

    // Remove duplicates
    const uniqueLoadPaths = [...new Set(finalLoadPaths)];

    return {
        // Processing options
        minifyTemplates: true,
        minifyStyles: true,
        removeComments: true,
        watchFiles: true,
        preserveWhitespace: false,

        // SCSS/SASS compilation options - AUTOMATIC
        compileSass: true,
        sassOptions: {
            loadPaths: uniqueLoadPaths,  // AUTOMATIC: includes node_modules, src, src/styles
            charset: true,
            quietDeps: true,
            verbose: false,
            sourceMap: false,
            ...(userOptions.sassOptions || {})
        },

        // Resource options
        resourceBaseUrl: '',
        processResources: true,

        // Logging options
        showProcessedFiles: false,

        logger: {
            enabled: true,
            level: 3,
            prefix: 'XCON-VITE',
            timestamp: true,
            colors: true,
            logLevel: 'info'
        },

        // File extension options
        fileExtensions: ['.ts', '.js'],
        templateExtensions: ['.html'],
        styleExtensions: ['.css', '.scss', '.sass', '.less'],

        // Minification with Terser
        useTerser: true,
        terserOptions: {
            compress: {
                passes: 2,
                drop_console: false,
                drop_debugger: true,
                pure_funcs: [],
                dead_code: true,
                conditionals: true,
                evaluate: true,
                booleans: true,
                loops: true,
                unused: true,
                hoist_funs: true,
                keep_fargs: false,
                hoist_vars: false,
                if_return: true,
                join_vars: true,
                collapse_vars: true,
                reduce_vars: true,
                negate_iife: true,
                pure_getters: false,
                unsafe: false,
                unsafe_comps: false,
                unsafe_proto: false,
                unsafe_regexp: false,
                unsafe_undefined: false,
                side_effects: true
            },
            mangle: {
                properties: false,
                toplevel: false,
                keep_classnames: false,
                keep_fnames: false,
                reserved: [],
                safari10: false
            },
            format: {
                comments: false,
                ascii_only: false,
                indent_level: 4,
                indent_start: 0,
                inline_script: true,
                keep_numbers: false,
                max_line_len: 1,
                preamble: undefined,
                preserve_annotations: false,
                quote_keys: false,
                quote_style: 0,
                safari10: false,
                semicolons: true,
                shebang: true,
                webkit: false,
                wrap_iife: false
            },
            sourceMap: true,
            toplevel: false,
            ie8: false,
            keep_classnames: false,
            keep_fnames: false,
            safari10: false
        },

        // Advanced options
        customProcessors: [],
        excludeFiles: [],
        includeFiles: [],

        // Enable source maps by default in development
        development: false,
        sourceMap: true,

        // Override with user options
        ...userOptions,

    } as Required<XConVitePluginOptions>;
}